import React, { FunctionComponent, useMemo } from 'react'
import { Text, View } from 'react-native'
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons'
import { DemandeAbsenceProps, DemandesState } from './DemandeAbsence.types'
import { useDemandeAbsenceStyles } from './DemandeAbsence.styles'
import { useDemandeAbsenceState } from './DemandeAbsence.states'
import { useNotify } from '../../components/Notification';
import { DetailDemandeAbsence } from '../../components/Absences/DetailDemandeAbsence/DetailDemandeAbsence.component'
import AddButton from '../../components/AddButton/AddButton'
import Kanban from '../../components/Kanban/Kanban'
import ElementKanbanDemande from './ElementKanbanDemande'
import SousElementKanbanDemande from './SousElementKanbanDemande'
import useEnTetePage from '../../components/EnTetePage/EnTetePage'
import dayjs from 'dayjs'
import { getStatusColor, getStatusIcon, getStatusTitle } from '../../components/StatutDemande/StatutDemande.constants'

export const DemandeAbsence: FunctionComponent<DemandeAbsenceProps> = ({ navigation }) => {
  const notify = useNotify();
  const { EnTete, dateDebutRef, dateFinRef } = useEnTetePage({
    text: "Demandes d'absences",
    periodGestion: {
      period : 'year',
      defaultDate: dayjs()
    }
  });
  const { styles } = useDemandeAbsenceStyles();
  const {
    demandes,
    loading,
    selectedDemande,
    handleVisualiseDemande,
    closeDemandeDetail,
    handleAnnule,
    handleValide,
  } = useDemandeAbsenceState( navigation, notify, dateDebutRef, dateFinRef)
  const formattedDemandes = useMemo(() => ({
    enCours: demandes.enCours.map((demande)=> ({
      ...demande,
      borderColor: demande.natureAbsence?.couleurHexa,
      libelleNature: demande.natureAbsence?.libelle,
      identifiant: demande.identifiant || 0
    })),
    acceptees: demandes.acceptees.map((demande)=> ({
      ...demande,
      borderColor: demande.natureAbsence?.couleurHexa,
      libelleNature: demande.natureAbsence?.libelle,
      identifiant: demande.identifiant || 0
    })),
    refusees: demandes.refusees.map((demande)=> ({
      ...demande,
      borderColor: demande.natureAbsence?.couleurHexa,
      libelleNature: demande.natureAbsence?.libelle,
      identifiant: demande.identifiant || 0
    })),
    annulees: demandes.annulees.map((demande)=> ({
      ...demande,
      borderColor: demande.natureAbsence?.couleurHexa,
      libelleNature: demande.natureAbsence?.libelle,
      identifiant: demande.identifiant || 0
    })),
  }), [demandes])

  const colonnesKanban = useMemo(() => (
    Object.keys(formattedDemandes).map((colonne) => ({
      title: getStatusTitle(colonne),
      elements: formattedDemandes[colonne as keyof DemandesState],
      logo: getStatusIcon(colonne, colonne !== "enCours"),
      logoColor: getStatusColor(colonne, colonne !== "enCours")
    }))
  ), [formattedDemandes])

  // Navigation vers la page de nouvelle demande
  const handleAddAbsence = () => {
    if (navigation) {
      try {
        navigation.navigate('NouvelleDemande');
      } catch (error) {
        console.error('❌ Erreur de navigation:', error);
      }
    } else {
      console.error('❌ Navigation non disponible');
    }
  };

  return (
    <View style={styles.container}>
      {EnTete}
      {loading ? (
        <View style={styles.loadingContainer}>
          <MaterialCommunityIcons name="loading" size={32} color="#3b82f6" />
          <Text style={styles.loadingText}>Chargement des demandes...</Text>
        </View>
      ) : (
        <View style={styles.contentContainer}>
          <Kanban
            colonnes={colonnesKanban}
            orderField='dateDebut'
            categoryField='libelleNature'
            borderWidth={4}
            noContentText='Aucune demande'
            content={(demande) => ElementKanbanDemande({demande, styles})}
            subContent={(demande) => SousElementKanbanDemande({demande})}
            onElementPressed={handleVisualiseDemande}
          />
        </View>
      )}
      {/* Détails de la demande */}
      {selectedDemande && (
        <DetailDemandeAbsence
          onClose={closeDemandeDetail}
          selectedDemande={selectedDemande}
          handleAnnule={handleAnnule}
          handleValide={handleValide}
        />
      )}
      {/* Bouton flottant pour ajouter une absence */}
      <AddButton onPress={handleAddAbsence}/>
    </View>
  )
}
